import { tss } from 'tss-react/mui';

import {
  getPriorityColor,
  type PriorityTypes
} from '@/components/PriorityChip/';
import type { PriorityDTO } from '@/pages/Ticket/Ticket.types';
import type { ThemeMode } from '@/types/theme.ts';

const useStyles = tss
  .withParams<{
    isLayoutMobile: boolean;
    priority: PriorityDTO;
    themeMode: ThemeMode;
  }>()
  .create(({ theme, isLayoutMobile, priority, themeMode }) => ({
    ticketDetailsSubTopbar: {
      backgroundColor: theme.vars.palette.background.elevation1,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      zIndex: 2
    },
    /* Top Content */
    subTopbarTopContent: {
      boxShadow: isLayoutMobile ? theme.shadows[2] : 'none',
      display: 'flex',
      flexDirection: isLayoutMobile ? 'column' : 'row',
      gap: theme.spacing(2),
      justifyContent: 'space-between',
      padding: theme.spacing(3),
      width: '100%'
    },
    topContentPriority: {
      alignItems: 'center',
      backgroundColor: priority
        ? `var(--mui-palette-${getPriorityColor(
            priority.id as PriorityTypes
          )}-main)`
        : theme.vars.palette.secondary.main,
      borderRadius: '0.25rem',
      color: theme.vars.palette.common.white,
      display: 'flex',
      fontSize: '1.5rem',
      fontWeight: 700,
      height: '3rem',
      justifyContent: 'center',
      minHeight: '3rem',
      minWidth: '3rem'
    },
    topContentOrganizationName: {
      display: 'inline',
      fontSize: '1rem',
      fontWeight: 'normal'
    },
    /* Bottom Content */
    ticketDetailsSubTopbarBottom: {
      ...(isLayoutMobile && {
        position: 'relative',
        top: '-1.45rem',
        left: '-1.5rem',
        width: '100vw'
      })
    },
    subTopbarBottomContent: {
      background: `repeating-linear-gradient(to bottom, ${
        themeMode === 'dark' ? '#333c4d' : '#eceff1'
      } 0 3.5rem, ${theme.vars.palette.border.light} 0 3.55rem)`,
      backgroundColor: themeMode === 'dark' ? '#333c4d' : '#eceff1',
      borderBottom: `0.0625rem solid ${theme.vars.palette.border.light}`,
      borderTop: `0.0625rem solid ${theme.vars.palette.border.light}`,
      boxShadow: isLayoutMobile ? 'none' : theme.shadows[2],
      display: 'flex',
      flexWrap: 'wrap',
      position: 'relative',
      width: '100%',
      [theme.breakpoints.down('lg')]: {
        display: 'block',
        '& > *': {
          background: theme.spacing(5)
        }
      }
    },
    bottomContentGrid: {
      alignItems: 'center',
      display: 'flex',
      padding: theme.spacing(0, 0, 0, 2),
      '& > *': {
        minWidth: '7rem',
        paddingLeft: theme.spacing(2)
      },
      [theme.breakpoints.down('lg')]: {
        alignItems: 'center',
        background: `repeating-linear-gradient(to bottom, ${
          themeMode === 'dark' ? '#333c4d' : '#eceff1'
        } 0 3.5rem, ${theme.vars.palette.border.light} 0 3.55rem)`,
        display: 'grid',
        gridAutoRows: '3.5rem',
        gridRowGap: '0.0625rem',
        gridTemplateColumns: 'repeat(auto-fill, minmax(13rem, max-content))'
      }
    },
    bottomContentBackButton: {
      alignItems: 'center',
      borderRadius: 0,
      display: 'flex',
      height: '100%',
      justifyContent: 'center',
      left: 0,
      minWidth: theme.spacing(3),
      padding: 0,
      position: 'absolute',
      top: 0,
      [theme.breakpoints.down('lg')]: {
        borderBottom: `0.0625rem solid ${theme.vars.palette.border.light}`
      }
    },
    bottomContentSection: {
      display: 'flex',
      height: '3.5rem',
      borderLeft: `0.0625rem solid ${theme.vars.palette.border.light}`,
      [theme.breakpoints.down('lg')]: {
        borderBottom: `0.0625rem solid ${theme.vars.palette.border.light}`,
        display: 'block',
        height: 'inherit'
      },
      ...(!isLayoutMobile && {
        marginLeft: `${theme.spacing(3)}`
      })
    },
    bottomContentSectionKeys: {
      color: theme.vars.palette.text.secondary,
      display: 'block'
    },
    bottomContentSectionValues: {
      fontWeight: 700
    }
  }));

export default useStyles;
